/**
 * Amount and date helpers for the one-time payment form.
 *
 * The API requires `scheduleDate` to be strictly after today in `America/New_York`, so every date
 * bound here is computed in that zone rather than the browser's. A member in Los Angeles at 22:00
 * is already on the next Eastern day, and naive local-date arithmetic would offer them a date the
 * server rejects.
 */

const PAYMENT_TIME_ZONE = 'America/New_York';

/** `en-CA` yields ISO-shaped `yyyy-MM-dd`, which is what the API expects. */
const ET_DATE_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: PAYMENT_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Today's calendar date in Eastern Time, as ISO `yyyy-MM-dd`. */
export function todayInEastern(now: Date = new Date()): string {
  return ET_DATE_FORMAT.format(now);
}

/**
 * The earliest date the member may schedule: tomorrow in Eastern Time. Derived by shifting a full
 * day and re-reading the Eastern calendar date, so it stays correct across daylight-saving
 * transitions without any offset arithmetic.
 */
export function earliestScheduleDate(now: Date = new Date()): string {
  return todayInEastern(new Date(now.getTime() + 24 * 60 * 60 * 1000));
}

/** True when an ISO `yyyy-MM-dd` value is strictly after today in Eastern Time. */
export function isAfterTodayInEastern(scheduleDate: string, now: Date = new Date()): boolean {
  return scheduleDate > todayInEastern(now);
}

/**
 * The last date a card can be charged, as ISO `yyyy-MM-dd`: a card stays valid through the final day
 * of its expiration month, so 09/2027 yields `2027-09-30`. Used to stop a member scheduling past the
 * point their chosen card dies, which neither the API nor the gateway reports back.
 */
export function lastChargeableDate(expirationMonth: number, expirationYear: number): string {
  // Day 0 of the following month is the last day of this one, and it rolls the year over for
  // December on its own.
  const lastDay = new Date(expirationYear, expirationMonth, 0).getDate();
  return `${expirationYear}-${String(expirationMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
}

/**
 * Digits allowed before the decimal point when no maximum is known yet. Wide enough not to fight a
 * legitimate amount while the `maximumPaymentAmount` query is still in flight.
 */
const DEFAULT_MAX_INTEGER_DIGITS = 7;

/** Integer digits in the cap, so the field cannot accept a number longer than the maximum allows. */
export function integerDigitsFor(maxAmount: number): number {
  return String(Math.floor(Math.abs(maxAmount))).length;
}

/**
 * Keeps only what can belong to a currency amount: digits and a single decimal point, capped at two
 * decimal places and at `maxIntegerDigits` before the point. Applied as the amount field's `format`,
 * so the member can neither type a malformed value nor run the field off the end of the box with a
 * number no cap would ever allow.
 */
export function formatAmountInput(value: string, maxIntegerDigits: number = DEFAULT_MAX_INTEGER_DIGITS): string {
  const cleaned = value.replace(/[^\d.]/g, '');
  const [whole, ...rest] = cleaned.split('.');
  const truncatedWhole = whole.slice(0, maxIntegerDigits);
  if (rest.length === 0) return truncatedWhole;
  return `${truncatedWhole}.${rest.join('').slice(0, 2)}`;
}

/**
 * Parses a typed amount to a number, or `null` when it is empty or not a number. Kept separate from
 * `useCurrency`, which only formats for display.
 */
export function parseAmount(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}
